import type { ArCohortMatrix, CashTroughAttribution, InsightCard, RevenueMovement, MetricComparisonResponse, QuickBooksCfoPresentationResponse, QuickBooksFpaStatementRender, QuickBooksFpaStatementRow } from '../../../../data/ServerApiClient'
import { formatValue } from './format'
import { pairFor } from './metricPairs'
import { HIGHLIGHT_TEMPLATE_REGISTRY } from './registry'
import type { HighlightEvidencePoint, HighlightTemplateDefinition, HighlightVisualEvidence } from './types'

const numeric = (value: unknown): number | null => Number.isFinite(Number(value)) ? Number(value) : null
const clean = (value: unknown): string => String(value ?? '').trim()
const norm = (value: unknown): string => clean(value).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
const compareText = (left: string, right: string): number => left < right ? -1 : left > right ? 1 : 0
const compactHash = (value: string): string => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return `fgve_${(hash >>> 0).toString(36)}`
}

// One formatter for the whole visual layer. Previously this was `Intl`-based
// here and a second hand-rolled one in scene.ts, so the same number could be
// written two ways on one card — and `Intl` is ICU-dependent, which makes the
// browser preview and the exported PDF disagree.
const display = (value: number, unit: string): string => formatValue(value, unit)

const point = (
  input: Omit<HighlightEvidencePoint, 'rawValue' | 'displayValue' | 'unit' | 'reportingPeriod' | 'sourceLineage' | 'freshness' | 'provenanceState'> & {
    unit?: string
    reportingPeriod?: string | null
    sourceLineage?: string[]
    freshness?: HighlightEvidencePoint['freshness']
    provenanceState?: HighlightEvidencePoint['provenanceState']
  },
): HighlightEvidencePoint => {
  const unit = input.unit || 'count'
  return {
    ...input,
    rawValue: input.value,
    displayValue: display(input.value, unit),
    unit,
    reportingPeriod: input.reportingPeriod ?? input.period ?? null,
    sourceLineage: input.sourceLineage || [],
    freshness: input.freshness || 'current',
    provenanceState: input.provenanceState || 'governed',
    secondaryDisplayValue: input.secondaryDisplayValue ?? (input.secondaryValue == null ? null : display(input.secondaryValue, unit)),
  }
}

function evidence(
  card: InsightCard,
  template: HighlightTemplateDefinition,
  title: string,
  unit: string,
  periods: string[],
  points: HighlightEvidencePoint[],
  sourceLineage: string[],
  dataThrough: string | null,
  state?: Partial<Pick<HighlightVisualEvidence, 'freshness' | 'provenanceState' | 'polarity'>>,
): HighlightVisualEvidence {
  const enrichedPoints = points.map((item) => ({
    ...item,
    sourceLineage: item.sourceLineage.length ? item.sourceLineage : sourceLineage,
    freshness: state?.freshness || item.freshness,
    provenanceState: state?.provenanceState || item.provenanceState,
  }))
  // Labels are evidence presentation, not decoration. Excluding them meant a
  // governed vocabulary correction (for example Total Income → Total Revenue)
  // retained the old evidence hash and could survive in a reviewed baseline.
  const canonical = JSON.stringify({ templateId: template.id, unit, periods, points: enrichedPoints.map((item) => [item.id, item.label, item.rawValue, item.displayValue, item.secondaryValue, item.period, item.group, item.sourceLineage, item.freshness, item.provenanceState]) })
  return {
    id: `${card.id}:${template.id}`,
    kind: template.evidenceKind,
    title,
    unit,
    polarity: state?.polarity || template.polarity,
    periods,
    points: enrichedPoints,
    sourceLineage,
    dataThrough,
    freshness: state?.freshness || 'current',
    provenanceState: state?.provenanceState || 'governed',
    hash: compactHash(canonical),
  }
}

function statementFor(card: InsightCard, response: QuickBooksCfoPresentationResponse): QuickBooksFpaStatementRender | null {
  const metric = norm(card.impact?.metric_id)
  if (/cash|asset|liabil|equity|working_capital|receivable|inventory|payable/.test(metric)) return response.statements.balance_sheet
  return response.statements.profit_and_loss
}

/**
 * Does an increase in this row help or hurt operating income?
 *
 * Read from the governed classification, never from the label — "Total Other
 * Income" and "Interest Expense" both contain words that a regex gets wrong.
 * Returns null when the classification is absent, and a null polarity renders
 * neutral rather than guessing a colour.
 */
function increaseIsFavourable(row: QuickBooksFpaStatementRow): boolean | null {
  const classification = norm(row.qbo_classification)
  if (classification === 'revenue' || classification === 'income') return true
  if (classification === 'expense' || classification === 'cost_of_goods_sold') return false
  // Balance-sheet rows have no operating-income polarity; the caller decides.
  if (classification === 'asset' || classification === 'liability' || classification === 'equity') return null
  const type = norm(row.qbo_account_type)
  if (/income|revenue/.test(type)) return true
  if (/expense|cost_of_goods/.test(type)) return false
  return null
}

/**
 * Rows that can be plotted side by side without counting the same money twice.
 *
 * This is the correction that matters most. The first implementation selected
 * `row.is_total`, which returns the SUBTOTAL rows — and subtotals nest, so
 * "Total Wages" ⊂ "Total Payroll Expenses" ⊂ "Total Expenses" were plotted as
 * peers, and one driver appeared as five near-identical bars.
 *
 * The fix is not "leaves only": on a balance sheet, Cash / A/R / A/P are
 * rollups that do NOT nest, and they are exactly the rows a working-capital
 * visual wants. The real invariant is that no selected row may be an ancestor
 * of another selected row.
 *
 * So: drop any row that has a descendant in the same set. What survives is a
 * maximal antichain — mutually exclusive, and additive against their common
 * parent. When a layout carries no hierarchy at all, nothing is an ancestor of
 * anything and every row survives, which is the correct degenerate case.
 */
function mutuallyExclusiveRows(rows: QuickBooksFpaStatementRow[]): QuickBooksFpaStatementRow[] {
  const candidates = rows.filter((row) => !row.is_formula)
  const byId = new Map(candidates.map((row) => [clean(row.node_id), row]))
  const hasDescendant = new Set<string>()

  for (const row of candidates) {
    // Walk to the root marking every ancestor as covered by this row.
    let parent = clean(row.parent_node_id)
    const seen = new Set<string>()
    while (parent && byId.has(parent) && !seen.has(parent)) {
      seen.add(parent)
      hasDescendant.add(parent)
      parent = clean(byId.get(parent)?.parent_node_id)
    }
  }

  const antichain = candidates.filter((row) => !hasDescendant.has(clean(row.node_id)))
  return antichain.length ? antichain : candidates
}

/** Movement of every posting account between two periods, ranked by magnitude. */
function statementVariancePoints(
  statement: QuickBooksFpaStatementRender | null,
  options: { latestIndex?: number; topN?: number; unit?: string } = {},
): HighlightEvidencePoint[] {
  if (!statement || statement.periods.length < 2) return []
  const unit = options.unit || 'usd'
  const topN = options.topN ?? 6
  const latest = options.latestIndex ?? statement.periods.length - 1
  const prior = latest - 1
  if (prior < 0 || latest >= statement.periods.length) return []

  const movements = mutuallyExclusiveRows(statement.rows)
    .filter((row) => numeric(row.values[latest]) != null || numeric(row.values[prior]) != null)
    .map((row) => {
      const current = numeric(row.values[latest]) || 0
      const previous = numeric(row.values[prior]) || 0
      const value = current - previous
      const favourableIncrease = increaseIsFavourable(row)
      // Polarity correction: the displayed direction is the effect on operating
      // income, not the arithmetic sign. An expense falling is a negative delta
      // and a good month.
      const favourable = favourableIncrease == null ? null : favourableIncrease ? value > 0 : value < 0
      return {
        row,
        value,
        current,
        favourable,
      }
    })
    .filter((entry) => entry.value !== 0)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || compareText(a.row.node_id, b.row.node_id))

  const head = movements.slice(0, topN)
  const tail = movements.slice(topN)

  const points = head.map((entry) => point({
    id: entry.row.node_id,
    label: clean(entry.row.label),
    value: entry.value,
    unit,
    secondaryValue: entry.current,
    period: statement.periods[latest],
    status: entry.favourable == null ? 'neutral' : entry.favourable ? 'good' : 'bad',
    favourable: entry.favourable,
    sourceLineage: ['quickbooks_cfo_statements_render', entry.row.node_id],
  }))

  // Never silently truncate. The remainder is a real number and the reader is
  // entitled to see that it exists.
  if (tail.length) {
    const remainder = tail.reduce((sum, entry) => sum + entry.value, 0)
    if (remainder !== 0) {
      points.push(point({
        id: `${statement.statement_type}:all_other`,
        label: `All other (${tail.length})`,
        value: remainder,
        unit,
        period: statement.periods[latest],
        status: 'neutral',
        favourable: null,
        sourceLineage: ['quickbooks_cfo_statements_render'],
      }))
    }
  }
  return points
}

/**
 * Two governed series for the dual-axis trend.
 *
 * Both series come from the same `metric_comparisons` payload, so they share a
 * period axis by construction and cannot be silently misaligned. Returns an
 * empty array unless the card's metric has a *declared* partner — a two-axis
 * chart of an undeclared pair is the easiest way in the library to imply a
 * relationship that is not there.
 */
function dualAxisPoints(
  card: InsightCard,
  comparisons: MetricComparisonResponse | null | undefined,
): HighlightEvidencePoint[] {
  const pair = pairFor(clean(card.impact?.metric_id))
  if (!pair || !comparisons?.metrics?.length) return []

  const seriesFor = (metricId: string) => {
    const row = comparisons.metrics.find((entry) => norm(entry.id) === norm(metricId))
    const trend = (row?.trend || []).filter((point) => numeric(point.value) != null)
    return { row, trend }
  }

  const primary = seriesFor(pair.primary)
  const secondary = seriesFor(pair.secondary)
  if (!primary.row || !secondary.row) return []

  // Only periods both series observe. A point present on one axis and absent on
  // the other reads as a divergence that never happened.
  const shared = primary.trend
    .map((point) => clean(point.period))
    .filter((period) => secondary.trend.some((point) => clean(point.period) === period))
  if (shared.length < 4) return []

  const build = (
    side: ReturnType<typeof seriesFor>,
    group: string,
    unit: string,
  ): HighlightEvidencePoint[] => shared.map((period) => {
    const point = side.trend.find((entry) => clean(entry.period) === period)
    return {
      id: `${group}:${period}`,
      label: clean(side.row?.label) || group,
      value: numeric(point?.value) || 0,
      unit,
      period,
      group,
      sourceLineage: ['quickbooks_cfo_metric_comparisons', clean(side.row?.id)],
    }
  }).map(point)

  return [
    ...build(primary, pair.primary, pair.primaryUnit),
    ...build(secondary, pair.secondary, pair.secondaryUnit),
  ]
}

/**
 * Working-capital bridge: net income → each working-capital movement → operating cash.
 *
 * **Reconciles by construction.** The cash-flow compiler emits `cash_flow:net_income`,
 * one already-sign-flipped operating detail row per balance-sheet node, and the
 * `cash_flow:operating` total — and builds the total as
 * `net_income + Σ(operating detail)`. So this is a filter and a sort, not a
 * re-derivation, and the bridge cannot disagree with the statement it came from.
 *
 * Gives `working_capital_current_map` its own evidence instead of the generic
 * statement-variance array it shared with four other templates.
 */
function workingCapitalBridgePoints(
  statement: QuickBooksFpaStatementRender | null,
): HighlightEvidencePoint[] {
  if (!statement?.rows?.length || !statement.periods.length) return []
  const latest = statement.periods.length - 1
  const at = (row: QuickBooksFpaStatementRow) => numeric(row.values[latest])

  const netIncomeRow = statement.rows.find((row) => clean(row.node_id) === 'cash_flow:net_income')
  const operatingTotal = statement.rows.find((row) => clean(row.node_id) === 'cash_flow:operating' && row.is_total)
  if (!netIncomeRow || at(netIncomeRow) == null) return []

  // Operating detail rows only. The section total is the bridge's endpoint, not
  // a step, and including it would double the whole operating movement.
  const movements = statement.rows.filter((row) =>
    norm(row.cash_flow_section) === 'operating'
    && !row.is_total
    && clean(row.node_id) !== 'cash_flow:net_income'
    && at(row) != null
    && at(row) !== 0)

  if (!movements.length) return []

  const points: HighlightEvidencePoint[] = [
    point({
      id: 'cash_flow:net_income',
      label: clean(netIncomeRow.label) || 'Net income',
      value: at(netIncomeRow) || 0,
      absolute: true,
      unit: 'usd',
      period: statement.periods[latest],
      status: (at(netIncomeRow) || 0) >= 0 ? 'good' : 'bad',
      favourable: (at(netIncomeRow) || 0) >= 0,
      sourceLineage: ['quickbooks_cfo_statements_render', 'cash_flow:net_income'],
    }),
  ]

  // Largest movements first; the rest folded into a named remainder so the
  // bridge still sums to the operating total.
  const ranked = [...movements].sort((a, b) => Math.abs(at(b) || 0) - Math.abs(at(a) || 0) || compareText(clean(a.node_id), clean(b.node_id)))
  const head = ranked.slice(0, 5)
  const tail = ranked.slice(5)

  for (const row of head) {
    const value = at(row) || 0
    points.push(point({
      id: clean(row.node_id),
      label: clean(row.label),
      value,
      unit: 'usd',
      period: statement.periods[latest],
      // The compiler already flipped the sign to a cash effect, so a positive
      // value releases cash and a negative one consumes it. No polarity lookup
      // is needed or correct here.
      status: value >= 0 ? 'good' : 'bad',
      favourable: value >= 0,
      sourceLineage: ['quickbooks_cfo_statements_render', clean(row.node_id)],
    }))
  }
  if (tail.length) {
    const remainder = tail.reduce((sum, row) => sum + (at(row) || 0), 0)
    if (remainder !== 0) {
      points.push(point({
        id: 'cash_flow:operating:all_other',
        label: `All other (${tail.length})`,
        value: remainder,
        unit: 'usd',
        period: statement.periods[latest],
        status: 'neutral',
        favourable: null,
        sourceLineage: ['quickbooks_cfo_statements_render'],
      }))
    }
  }

  // Endpoint, when the statement publishes it. Not synthesized: if the total is
  // absent the bridge simply ends on its last step rather than inventing a sum.
  if (operatingTotal && at(operatingTotal) != null) {
    points.push(point({
      id: 'cash_flow:operating',
      label: clean(operatingTotal.label) || 'Operating cash',
      value: at(operatingTotal) || 0,
      absolute: true,
      unit: 'usd',
      period: statement.periods[latest],
      status: 'neutral',
      favourable: null,
      sourceLineage: ['quickbooks_cfo_statements_render', 'cash_flow:operating'],
    }))
  }
  return points
}

/** Statement rollup rows, in reading order, keyed by their governed canonical row id. */
const PNL_BRIDGE_STEPS: ReadonlyArray<{ key: string; label: string; favourableWhenUp: boolean }> = [
  { key: 'fpa:pnl:total_revenue', label: 'Revenue', favourableWhenUp: true },
  { key: 'fpa:pnl:total_cost_of_sales', label: 'Cost of sales', favourableWhenUp: false },
  { key: 'fpa:pnl:total_expenses', label: 'Operating expenses', favourableWhenUp: false },
  { key: 'fpa:pnl:net_other_income', label: 'Other income', favourableWhenUp: true },
]

/**
 * Prior result → each rollup movement → current result.
 *
 * A different question from `statementVariancePoints`, which ranks individual
 * accounts. This walks the P&L's own calculation scheme, so it answers "how did
 * the result get from there to here" rather than "which account moved most".
 *
 * Giving `legacy_waterfall_bridge` this instead of the shared account array is
 * the substance of the fix for five templates drawing one picture: the
 * diversity rule was comparing metadata while the data underneath was identical.
 */
function pnlBridgePoints(statement: QuickBooksFpaStatementRender | null): HighlightEvidencePoint[] {
  if (!statement || statement.periods.length < 2) return []
  const latest = statement.periods.length - 1
  const prior = latest - 1

  // `StatementRenderRow` publishes the canonical P&L identities in `node_id`
  // (`fpa:pnl:net_income`, `fpa:pnl:total_revenue`, ...). An older frontend
  // type also advertised an optional `semantic_key`, but the server contract
  // has never serialized that field. Looking only at the phantom field made
  // every real presentation miss this bridge and fall through to a ranked
  // account-variance chart that double-counted statement subtotals. Prefer the
  // served canonical id; keep the optional alias only for provider projections
  // that may add it explicitly.
  const rowFor = (key: string) =>
    statement.rows.find((row) => clean(row.node_id) === key || clean(row.semantic_key) === key)
  const netIncome = rowFor('fpa:pnl:net_income')
  if (!netIncome) return []
  const startValue = numeric(netIncome.values[prior])
  const endValue = numeric(netIncome.values[latest])
  if (startValue == null || endValue == null) return []

  const steps = PNL_BRIDGE_STEPS.flatMap((step) => {
    const row = rowFor(step.key)
    if (!row) return []
    const current = numeric(row.values[latest])
    const previous = numeric(row.values[prior])
    if (current == null || previous == null) return []
    const movement = current - previous
    if (movement === 0) return []
    // The bridge is expressed in effect-on-profit, so a cost increase is a
    // negative step even though the cost line itself went up.
    const effect = step.favourableWhenUp ? movement : -movement
    return [point({
      id: clean(row.node_id) || step.key,
      // Provider statements often call the revenue subtotal "Total Income".
      // That is an accounting-feed label, not CFO-facing language, and in a
      // profit bridge it is easily confused with Net Income. The governed
      // canonical row id tells us exactly what the line is, so present the
      // shared business label instead of leaking provider vocabulary.
      label: step.key === 'fpa:pnl:total_revenue' ? 'Total Revenue' : step.label,
      value: effect,
      unit: 'usd',
      period: statement.periods[latest],
      status: effect >= 0 ? 'good' : 'bad',
      favourable: effect >= 0,
      sourceLineage: ['quickbooks_cfo_statements_render', step.key],
    })]
  })
  if (!steps.length) return []

  // Anchors so the bridge shows where it started and landed. Any gap between
  // the steps and the endpoints is real and must not be hidden.
  const bridged = steps.reduce((sum, step) => sum + step.value, 0)
  const residual = endValue - startValue - bridged
  const points = [
    point({
      id: 'bridge:start',
      absolute: true,
      // The renderer prints the actual period below each endpoint. "Prior" and
      // "current" were ambiguous and consumed the label width without telling
      // the reader which months were being compared.
      label: clean(netIncome.label) || 'Net Income',
      value: startValue,
      unit: 'usd',
      period: statement.periods[prior],
      status: 'neutral',
      favourable: null,
      sourceLineage: ['quickbooks_cfo_statements_render', 'fpa:pnl:net_income'],
    }),
    ...steps,
  ]
  if (Math.abs(residual) > 0.01) {
    points.push(point({
      id: 'bridge:residual',
      label: 'Unexplained',
      value: residual,
      unit: 'usd',
      period: statement.periods[latest],
      status: 'watch',
      favourable: null,
      sourceLineage: ['quickbooks_cfo_statements_render'],
    }))
  }
  points.push(point({
    id: 'bridge:end',
    absolute: true,
    label: clean(netIncome.label) || 'Net Income',
    value: endValue,
    unit: 'usd',
    period: statement.periods[latest],
    status: 'neutral',
    favourable: null,
    sourceLineage: ['quickbooks_cfo_statements_render', 'fpa:pnl:net_income'],
  }))
  return points
}

/**
 * Each account against its own trailing average — a benchmark, not a variance.
 *
 * The third distinct cut of the same statement. "Which accounts are furthest
 * from their own normal" is a different question from "which moved most since
 * last month": a line that is reliably large does not appear here, and a modest
 * line that has doubled against its own history does.
 */
function trailingBenchmarkPoints(statement: QuickBooksFpaStatementRender | null): HighlightEvidencePoint[] {
  if (!statement || statement.periods.length < 4) return []
  const latest = statement.periods.length - 1
  // Baseline excludes the current period: including a value in the average it
  // is being judged against damps exactly the deviation being measured.
  const baselineIndices = statement.periods.map((_, index) => index).filter((index) => index < latest)
  if (baselineIndices.length < 3) return []

  return mutuallyExclusiveRows(statement.rows)
    .flatMap((row) => {
      const current = numeric(row.values[latest])
      if (current == null) return []
      const history = baselineIndices.map((index) => numeric(row.values[index])).filter((value): value is number => value != null)
      if (history.length < 3) return []
      const mean = history.reduce((sum, value) => sum + value, 0) / history.length
      if (Math.abs(mean) < 1) return []
      const deviation = current - mean
      if (deviation === 0) return []
      const favourableIncrease = increaseIsFavourable(row)
      const favourable = favourableIncrease == null ? null : favourableIncrease ? deviation > 0 : deviation < 0
      return [point({
        id: clean(row.node_id),
        label: clean(row.label),
        value: deviation,
        unit: 'usd',
        // The comparative marker the lollipop draws against.
        secondaryValue: mean,
        period: statement.periods[latest],
        status: favourable == null ? 'neutral' : favourable ? 'good' : 'bad',
        favourable,
        sourceLineage: ['quickbooks_cfo_statements_render', clean(row.node_id)],
      })]
    })
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || compareText(a.id, b.id))
    .slice(0, 6)
}

/**
 * The governed target a progress ring may fill against, or `null`.
 *
 * Only real, sourced references count: the healthy band published on the
 * matching ratio card. There is deliberately no fallback constant — an
 * unsourced denominator is what made the ring meaningless, and returning null
 * so the template is skipped is strictly better than drawing a confident ring
 * against a number nobody chose.
 */
function progressReference(
  card: InsightCard,
  response: QuickBooksCfoPresentationResponse,
): { target: number; label: string; lineage: string[] } | null {
  const metric = norm(card.impact?.metric_id)
  if (!metric) return null
  const cards = response.ratio_cards || null
  if (!cards) return null
  const match = Object.values(cards).find((entry: any) => norm(entry?.id) === metric || norm(entry?.label) === metric)
  const benchmark = (match as any)?.benchmark
  if (!benchmark) return null

  // Prefer the explicit benchmark value; otherwise the near edge of the healthy
  // band, chosen by which direction is good for THIS ratio rather than assumed.
  const explicit = numeric(benchmark.value)
  if (explicit != null && explicit !== 0) {
    return { target: explicit, label: clean(benchmark.source_label) || 'benchmark', lineage: ['cfo_ratio_cards', clean((match as any).id), 'benchmark.value'] }
  }
  const higherIsBetter = (match as any)?.higher_is_better
  const min = numeric(benchmark.healthy_min)
  const max = numeric(benchmark.healthy_max)
  const edge = higherIsBetter === false ? max : higherIsBetter === true ? min : (min ?? max)
  if (edge == null || edge === 0) return null
  return { target: edge, label: 'healthy range', lineage: ['cfo_ratio_cards', clean((match as any).id), 'benchmark.healthy_band'] }
}

/**
 * The trough week and the named events that create it.
 *
 * Reads the served `cash_trough` attribution rather than re-deriving anything
 * from the weekly table, so the calendar and the 13-week grid cannot disagree.
 * Each event keeps its provenance, which is what separates "a payroll run and a
 * quarterly premium you already owe" from "a modeled estimate".
 */
function cashCollisionPoints(
  response: QuickBooksCfoPresentationResponse,
): HighlightEvidencePoint[] {
  const attribution = (response as any).cash_trough as CashTroughAttribution | null | undefined
  if (!attribution?.drivers?.length) return []

  const events = attribution.drivers
    .flatMap((driver) => (driver.top_events || []).map((event) => ({ driver, event })))
    .filter(({ event }) => Number.isFinite(event.amount) && event.amount !== 0)
    // Outflows first — they are what creates a collision — then by week so the
    // calendar reads left to right in time.
    .sort((a, b) => a.event.amount - b.event.amount || compareText(a.event.week_start, b.event.week_start))
    .slice(0, 6)
  if (!events.length) return []

  return events.map(({ driver, event }) => point({
    id: `${driver.row_key}:${event.week_start}:${norm(event.label)}`,
    label: clean(event.counterparty_name) || clean(event.label) || clean(driver.label),
    value: event.amount,
    unit: 'usd',
    period: event.week_start,
    // `provenance` is the honest confidence signal: an already-issued document
    // is not the same claim as a modeled estimate, and the calendar says which.
    group: event.provenance,
    status: event.amount < 0 ? 'bad' : 'good',
    favourable: event.amount >= 0,
    sourceLineage: ['quickbooks_cfo_cash_planning', 'cash_trough', driver.row_key],
  }))
}

/**
 * Collections velocity: how each month's billings drain over time.
 *
 * One series per cohort, plotted against age rather than calendar date, so
 * cohorts are directly comparable. Only observable cells are emitted — an
 * unripe cell is unknown, not zero, and a curve drawn through zeros would show
 * collections improving simply because recent months have not aged yet.
 */
function collectionsVelocityPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const matrix = (response.ar_analysis as any)?.cohorts as ArCohortMatrix | null | undefined
  if (!matrix?.readable || !matrix.rows?.length) return []

  // Most recent cohorts with enough maturity to say something.
  const usable = matrix.rows
    .filter((row) => row.billed_amount > 0 && row.cells.some((cell) => cell.observable && cell.open_share != null))
    .slice(-4)
  if (usable.length < 2) return []

  return usable.flatMap((row) =>
    row.cells
      .filter((cell) => cell.observable && cell.open_share != null)
      .map((cell) => point({
        id: `${row.cohort_month}:${cell.age_days}`,
        label: `${cell.age_days}d`,
        value: (cell.open_share as number) * 100,
        unit: 'pct',
        period: row.cohort_month,
        // One group per cohort — the renderer draws a line per group.
        group: row.cohort_month,
        // Lower open share is better: the money came in.
        status: (cell.open_share as number) <= 0.25 ? 'good' : (cell.open_share as number) >= 0.6 ? 'bad' : 'watch',
        favourable: (cell.open_share as number) <= 0.25,
        sourceLineage: ['quickbooks_cfo_ar_analysis', 'cohorts', row.cohort_month],
      })),
  )
}

/**
 * Contribution by service line — revenue, direct cost, and the margin between.
 *
 * Sums the governed node pairings rather than guessing which accounts belong to
 * which line from labels. Lines whose pairing yields no revenue are dropped: a
 * configured-but-unused line is not a zero-margin line, and plotting it as one
 * would put an empty tile beside real ones as though they were comparable.
 */
function serviceLineMosaicPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const lines = response.service_lines
  const statement = response.statements?.profit_and_loss
  if (!lines?.length || !statement?.rows?.length || !statement.periods.length) return []
  const latest = statement.periods.length - 1

  const byNode = new Map(statement.rows.map((row) => [clean(row.node_id), row]))
  const sumNodes = (ids: string[]): number | null => {
    let total = 0
    let seen = 0
    for (const id of ids || []) {
      const row = byNode.get(clean(id))
      const value = row ? numeric(row.values[latest]) : null
      if (value == null) continue
      total += value
      seen += 1
    }
    return seen ? total : null
  }

  const tiles = lines.flatMap((line) => {
    const revenue = sumNodes(line.income_node_ids)
    if (revenue == null || revenue === 0) return []
    const cost = sumNodes(line.cogs_node_ids) ?? 0
    const contribution = revenue - Math.abs(cost)
    return [{ line, revenue, contribution }]
  })
  if (tiles.length < 2) return []

  return tiles
    .sort((a, b) => b.revenue - a.revenue || compareText(clean(a.line.id), clean(b.line.id)))
    .slice(0, 6)
    .map(({ line, revenue, contribution }) => point({
      id: clean(line.id),
      label: clean(line.name),
      // Tile area is revenue; the contribution margin is the comparative read.
      value: revenue,
      secondaryValue: contribution,
      unit: 'usd',
      period: statement.periods[latest],
      status: contribution < 0 ? 'bad' : contribution / revenue < 0.2 ? 'watch' : 'good',
      favourable: contribution >= 0,
      sourceLineage: ['quickbooks_cfo_service_lines', clean(line.id)],
    }))
}

/**
 * A ratio against its published healthy band.
 *
 * Reads the same governed `ratio_cards` benchmark the progress ring uses, so
 * the two can never disagree about where healthy starts. A card with only one
 * edge of the band is still renderable — a floor with no ceiling is a real
 * shape — but a card with neither is not, and is skipped rather than drawn
 * against an invented range.
 */
function healthRangePoints(
  card: InsightCard,
  response: QuickBooksCfoPresentationResponse,
  dataThrough: string | null,
): HighlightEvidencePoint[] {
  const metric = norm(card.impact?.metric_id)
  const cards = response.ratio_cards || null
  if (!metric || !cards) return []
  const match = Object.values(cards).find((entry: any) => norm(entry?.id) === metric || norm(entry?.label) === metric) as any
  const benchmark = match?.benchmark
  if (!benchmark) return []

  const min = numeric(benchmark.healthy_min)
  const max = numeric(benchmark.healthy_max)
  if (min == null && max == null) return []
  const current = numeric(card.impact?.value)
  if (current == null) return []

  const unit = clean(card.impact?.unit) || 'ratio'
  const inBand = (min == null || current >= min) && (max == null || current <= max)
  const points: HighlightEvidencePoint[] = [point({
    id: `${clean(match.id)}:current`,
    label: clean(match.label) || card.headline,
    value: current,
    unit,
    period: dataThrough,
    status: inBand ? 'good' : 'bad',
    favourable: inBand,
    sourceLineage: ['cfo_ratio_cards', clean(match.id)],
  })]
  // The band edges travel as their own points so the renderer draws the real
  // range rather than a shaded guess around the value.
  if (min != null) {
    points.push(point({ id: `${clean(match.id)}:healthy_min`, label: 'Healthy floor', value: min, unit, period: dataThrough, status: 'neutral', favourable: null, group: 'band', sourceLineage: ['cfo_ratio_cards', clean(match.id), 'benchmark.healthy_min'] }))
  }
  if (max != null) {
    points.push(point({ id: `${clean(match.id)}:healthy_max`, label: 'Healthy ceiling', value: max, unit, period: dataThrough, status: 'neutral', favourable: null, group: 'band', sourceLineage: ['cfo_ratio_cards', clean(match.id), 'benchmark.healthy_max'] }))
  }
  return points
}

/**
 * Plan attainment by line, for the latest period that can actually be judged.
 *
 * Uses the LAST CLOSED period, never the newest one. A partial month carries a
 * whole month of plan against part of a month of actuals, so it always reads as
 * a miss — which is a completeness artifact, not a decision. The server marks
 * each period `closed` / `partial` / `future`; this trusts that marking rather
 * than re-deriving it, so the rail and the plan page cannot disagree.
 */
function planDecisionPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const plan = response.plan_vs_actual
  if (!plan?.rows?.length) return []
  // Blocked books must not be scored against plan at all.
  if (norm(plan.actuals_integrity) === 'blocked') return []

  const judged = plan.rows
    .flatMap((row) => {
      const period = [...(row.periods || [])].reverse().find((entry) => norm(entry.period_status) === 'closed')
      if (!period) return []
      const actual = numeric(period.actual)
      const target = numeric(period.plan)
      if (actual == null || target == null || target === 0) return []
      return [{ row, period, actual, target }]
    })
    // Largest absolute gap first — that is what a decision rail is for.
    .sort((a, b) => Math.abs(b.actual - b.target) - Math.abs(a.actual - a.target) || compareText(clean(a.row.node_id), clean(b.row.node_id)))
    .slice(0, 6)
  if (judged.length < 2) return []

  return judged.map(({ row, period, actual, target }) => {
    // `favorability` is the server's governed read; an expense under plan is
    // good and a regex on the label would get that wrong.
    const favourable = norm(period.favorability) === 'favorable'
      ? true
      : norm(period.favorability) === 'unfavorable' ? false : null
    return point({
      id: clean(row.node_id),
      label: clean(row.label),
      value: actual,
      // The plan line the rail measures against.
      secondaryValue: target,
      unit: 'usd',
      period: period.period_start,
      status: favourable == null ? 'neutral' : favourable ? 'good' : 'bad',
      favourable,
      sourceLineage: ['quickbooks_cfo_plan_vs_actual', clean(row.node_id)],
    })
  })
}

/**
 * Equity value by method — the football field.
 *
 * One bar per valuation framework, spanning its own low-to-high range. Methods
 * with no published band are dropped rather than collapsed to a point: a DCF
 * that produced a single number is not a range, and drawing it as a zero-width
 * bar beside real ranges would imply a precision it does not claim.
 */
function valuationFieldPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const frameworks = response.valuation?.frameworks
  if (!frameworks?.length) return []

  const bars = frameworks.flatMap((framework) => {
    const low = numeric(framework.equity_value_low)
    const high = numeric(framework.equity_value_high)
    const mid = numeric(framework.equity_value_mid) ?? numeric(framework.equity_value)
    if (low == null || high == null || mid == null) return []
    if (high <= low) return []
    return [{ framework, low, mid, high }]
  })
  if (bars.length < 2) return []

  return bars
    .sort((a, b) => b.mid - a.mid || compareText(clean(a.framework.id), clean(b.framework.id)))
    .slice(0, 6)
    .map(({ framework, low, mid, high }) => point({
      id: clean(framework.id),
      label: clean(framework.label) || clean(framework.id),
      // The point value is the method's midpoint; the band travels alongside so
      // the renderer draws a real span rather than a bar with error whiskers
      // guessed from it.
      value: mid,
      secondaryValue: high - low,
      unit: 'usd',
      period: null,
      status: 'neutral',
      favourable: null,
      sourceLineage: ['quickbooks_cfo_valuation', clean(framework.id)],
    }))
}

/**
 * Revenue movement staircase: prior revenue, each movement bucket, current.
 *
 * Refuses to draw when the server reports `reconciles: false`. A staircase whose
 * steps do not close the gap is the one chart shape where a hidden residual is
 * invisible — the bars simply look slightly off — so an unreconciled
 * decomposition must not render at all.
 */
function revenueMovementPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const movement = (response.ar_analysis as any)?.revenueMovement as RevenueMovement | null | undefined
  if (!movement?.buckets?.length || !movement.reconciles) return []

  const favourableByCategory: Record<string, boolean> = {
    new: true, reactivation: true, expansion: true, contraction: false, churn: false,
  }
  const label: Record<string, string> = {
    new: 'New customers', reactivation: 'Returning', expansion: 'Expansion',
    contraction: 'Contraction', churn: 'Churn',
  }

  return [
    point({
      id: 'movement:start', label: `Prior · ${movement.prior_period}`, value: movement.prior_revenue,
      absolute: true, unit: 'usd', period: movement.prior_period, status: 'neutral', favourable: null,
      sourceLineage: ['quickbooks_cfo_ar_analysis', 'revenueMovement'],
    }),
    ...movement.buckets.map((bucket) => point({
      id: `movement:${bucket.category}`,
      // The named driver, when there is a dominant one, beats a category label.
      label: bucket.top_customers?.length === 1
        ? `${label[bucket.category] || bucket.category} · ${clean(bucket.top_customers[0].customer)}`
        : `${label[bucket.category] || bucket.category} (${bucket.customer_count})`,
      value: bucket.amount,
      unit: 'usd',
      period: movement.current_period,
      status: favourableByCategory[bucket.category] ? 'good' : 'bad',
      favourable: favourableByCategory[bucket.category] ?? null,
      sourceLineage: ['quickbooks_cfo_ar_analysis', 'revenueMovement', bucket.category],
    })),
    point({
      id: 'movement:end', label: `Current · ${movement.current_period}`, value: movement.current_revenue,
      absolute: true, unit: 'usd', period: movement.current_period, status: 'neutral', favourable: null,
      sourceLineage: ['quickbooks_cfo_ar_analysis', 'revenueMovement'],
    }),
  ]
}

/**
 * Class revenue by period — the pressure matrix.
 *
 * **Revenue only.** QuickBooks classes tag invoice lines and have no
 * expense-side counterpart, so this cannot answer a margin or operating-cost
 * question. Each cell is that class's share of the period, which is what makes
 * the tiles comparable across classes of very different size: a large class
 * drifting two points and a small one halving are both visible.
 */
function classPressurePoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const matrix = (response.ar_analysis as any)?.classRevenue as { basis?: string; cells?: Array<{ class_id: string; class_name: string; period: string; revenue: number }> } | null | undefined
  const cells = matrix?.cells
  if (!cells?.length) return []

  const periods = Array.from(new Set(cells.map((cell) => cell.period))).sort()
  const classes = Array.from(new Set(cells.map((cell) => clean(cell.class_id))))
  if (periods.length < 3 || classes.length < 2) return []

  // Share of period, so classes of different scale sit on one colour ramp.
  const periodTotals = new Map(periods.map((period) => [
    period,
    cells.filter((cell) => cell.period === period).reduce((sum, cell) => sum + (numeric(cell.revenue) || 0), 0),
  ]))

  return cells
    .flatMap((cell) => {
      const revenue = numeric(cell.revenue)
      const total = periodTotals.get(cell.period) || 0
      if (revenue == null || total === 0) return []
      return [point({
        id: `${clean(cell.class_id)}:${cell.period}`,
        label: clean(cell.class_name) || clean(cell.class_id),
        value: (revenue / total) * 100,
        secondaryValue: revenue,
        unit: 'pct',
        period: cell.period,
        // One group per class — the matrix draws a row per group.
        group: clean(cell.class_id),
        status: 'neutral',
        favourable: null,
        sourceLineage: ['quickbooks_cfo_ar_analysis', 'classRevenue', clean(cell.class_id)],
      })]
    })
    .slice(0, 48)
}

/**
 * Forecast band: the published per-period interval, as published.
 *
 * Every value comes from the forecast lines — nothing is derived from a scalar
 * half-width, so the ribbon widens exactly as the model says it does. Periods
 * whose band is degenerate are dropped upstream, so a point forecast never
 * arrives here wearing a ribbon.
 */
function forecastBandPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const series = response.forecast_band
  if (!series?.points?.length || series.points.length < 3) return []
  return series.points.map((entry) => point({
    id: entry.period,
    label: entry.period,
    value: entry.center,
    // Band width at THIS period; the renderer draws low/high from it rather
    // than assuming a constant relative spread.
    secondaryValue: entry.high - entry.low,
    unit: 'usd',
    period: entry.period,
    status: 'neutral',
    favourable: null,
    sourceLineage: ['quickbooks_cfo_forecast_lines', clean(series.node_id), clean(series.scenario_key)],
  }))
}

/**
 * Named scenario trajectories.
 *
 * These are separately modelled futures — the server returns them only when the
 * package publishes more than one. Deliberately NOT built from the forecast
 * band: an interval is one model's uncertainty about a single future, and
 * drawing its edges as "upside" and "downside" would claim a planning exercise
 * that never happened. The two live in different fields for exactly that reason.
 */
function scenarioTrajectoryPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const series = response.forecast_scenarios
  if (!series?.scenarios?.length || series.scenarios.length < 2) return []
  const label: Record<string, string> = { base: 'Base', upside: 'Upside', downside: 'Downside' }
  return series.scenarios.flatMap((scenario) =>
    (scenario.points || []).map((entry) => point({
      id: `${scenario.scenario_key}:${entry.period}`,
      label: label[scenario.scenario_key] || clean(scenario.scenario_key),
      value: entry.value,
      unit: 'usd',
      period: entry.period,
      // One line per scenario.
      group: scenario.scenario_key,
      status: 'neutral',
      favourable: null,
      sourceLineage: ['quickbooks_cfo_forecast_lines', clean(series.node_id), scenario.scenario_key],
    })),
  )
}

function trendPoints(card: InsightCard): HighlightEvidencePoint[] {
  return (card.trend || []).map((item, index) => point({
    id: `${card.id}:${index}`,
    label: item.period,
    value: item.value,
    unit: card.impact.unit,
    period: item.period,
    sourceLineage: ['cfo_insights.trend', card.id],
  }))
}

function arConcernPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const asOf = response.ar_analysis?.asOfDate || response.source_state?.data_through || null
  return (response.ar_analysis?.topCustomerConcerns || response.ar_analysis?.customerTrends || [])
    .map((customer, index) => point({
      id: clean(customer.customerId) || `${norm(customer.customer)}:${index}`,
      label: clean(customer.customer) || `Customer ${index + 1}`,
      value: numeric(customer.endingAr) || 0,
      unit: 'usd',
      secondaryValue: numeric(customer.weightedAverageAge ?? customer.dso ?? customer.overdueAr) || 0,
      secondaryDisplayValue: `${Math.round(numeric(customer.weightedAverageAge ?? customer.dso) || 0)} days`,
      period: asOf,
      status: (numeric(customer.over90Ar) || 0) > 0 ? 'bad' : 'watch',
      sourceLineage: ['quickbooks_cfo_ar_analysis.customer_concerns', clean(customer.customerId)],
    }))
    .filter((item) => item.value !== 0)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || compareText(a.id, b.id))
    .slice(0, 12)
}

function agingPoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  return (response.ar_analysis?.periods || []).flatMap((period) => [
    point({ id: `${period.period}:current`, label: 'Current', value: numeric(period.currentAr) || 0, unit: 'usd', period: period.period, group: 'current', sourceLineage: ['quickbooks_cfo_ar_analysis.periods', period.period] }),
    point({ id: `${period.period}:under90`, label: '1–90 days', value: numeric(period.under90Ar) || 0, unit: 'usd', period: period.period, group: 'under90', sourceLineage: ['quickbooks_cfo_ar_analysis.periods', period.period] }),
    point({ id: `${period.period}:over90`, label: '90+ days', value: numeric(period.over90Ar) || 0, unit: 'usd', period: period.period, group: 'over90', status: 'bad', sourceLineage: ['quickbooks_cfo_ar_analysis.periods', period.period] }),
  ])
}

function arAgingBridgePoints(response: QuickBooksCfoPresentationResponse): HighlightEvidencePoint[] {
  const periods = response.ar_analysis?.periods || []
  if (periods.length < 2) return []
  const prior = periods.at(-2)!; const latest = periods.at(-1)!
  const buckets = [
    ['Current', numeric(prior.currentAr) || 0, numeric(latest.currentAr) || 0, 'good'],
    ['1–90 days', numeric(prior.under90Ar) || 0, numeric(latest.under90Ar) || 0, 'watch'],
    ['90+ days', numeric(prior.over90Ar) || 0, numeric(latest.over90Ar) || 0, 'bad'],
  ] as const
  const opening = buckets.reduce((sum, [, value]) => sum + value, 0)
  return [
    point({ id: `${prior.period}:opening`, label: 'Prior A/R', value: opening, unit: 'usd', period: prior.period, group: 'opening', sourceLineage: ['quickbooks_cfo_ar_analysis.periods', prior.period] }),
    ...buckets.map(([label, priorValue, latestValue, status]) => point({
      id: `${latest.period}:${norm(label)}`,
      label,
      value: latestValue - priorValue,
      unit: 'usd',
      period: latest.period,
      group: norm(label),
      status,
      sourceLineage: ['quickbooks_cfo_ar_analysis.periods', prior.period, latest.period],
    })),
  ]
}

/** Accepts detector-owned, provider-neutral typed evidence. This is the production
 * boundary for templates whose semantics cannot be inferred from statements. */
function structuredEvidence(
  card: InsightCard,
  template: HighlightTemplateDefinition,
  dataThrough: string | null,
  sourceState: Pick<HighlightVisualEvidence, 'freshness' | 'provenanceState'>,
): HighlightVisualEvidence | null {
  const raw = card.visual_evidence?.[template.id]
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.points)) return null
  const unit = clean(raw.unit) || clean(card.impact.unit) || 'count'
  const sourceLineage = Array.isArray(raw.source_lineage) ? raw.source_lineage.map(clean).filter(Boolean) : []
  if (!sourceLineage.length || sourceLineage.some((source) => /(^|[_.-])(demo|fixture|mock|sample)([_.-]|$)/i.test(source))) return null
  const points = raw.points.map((item: any, index: number) => {
    const value = numeric(item.raw_value ?? item.value)
    if (value == null) return null
    return point({
      id: clean(item.id) || `${card.id}:${template.id}:${index}`,
      label: clean(item.label) || `Item ${index + 1}`,
      value,
      unit: clean(item.unit) || unit,
      secondaryValue: numeric(item.secondary_value),
      period: clean(item.reporting_period || item.period) || null,
      group: clean(item.group) || null,
      status: ['good', 'watch', 'bad', 'neutral'].includes(item.status) ? item.status : undefined,
      sourceLineage: Array.isArray(item.source_lineage) ? item.source_lineage.map(clean).filter(Boolean) : sourceLineage,
      freshness: ['current', 'provisional', 'stale'].includes(item.freshness) ? item.freshness : (raw.freshness || sourceState.freshness),
      provenanceState: ['governed', 'estimated', 'unreconciled'].includes(item.provenance_state) ? item.provenance_state : (raw.provenance_state || sourceState.provenanceState),
    })
  }).filter(Boolean) as HighlightEvidencePoint[]
  const periods = Array.isArray(raw.periods) ? raw.periods.map(clean).filter(Boolean) : [...new Set(points.map((item) => clean(item.period)).filter(Boolean))]
  if (points.length < template.minimumPoints || new Set(periods).size < template.minimumPeriods) return null
  return evidence(card, template, clean(raw.title) || card.headline, unit, periods, points, sourceLineage, clean(raw.data_through) || dataThrough, {
    freshness: (['current', 'provisional', 'stale'].includes(clean(raw.freshness)) ? clean(raw.freshness) : sourceState.freshness) as HighlightVisualEvidence['freshness'],
    provenanceState: (['governed', 'estimated', 'unreconciled'].includes(clean(raw.provenance_state)) ? clean(raw.provenance_state) : sourceState.provenanceState) as HighlightVisualEvidence['provenanceState'],
    polarity: (['higher_is_better', 'lower_is_better', 'contextual'].includes(clean(raw.polarity)) ? clean(raw.polarity) : template.polarity) as HighlightVisualEvidence['polarity'],
  })
}

export type ReportingPeriodState = {
  /** Latest date the books actually contain data through. */
  dataThrough: string | null
  /**
   * True when `dataThrough` falls before the end of its own month, i.e. the
   * reporting period is still open.
   *
   * This matters far more than it looks. A month-over-month comparison against
   * a half-finished month always shows a collapse, because the revenue has not
   * been booked yet but the recurring costs have. Treating that as a finding
   * puts a fabricated crisis on the front page of a board pack — which is
   * exactly what shipped. `selection.ts` uses this as a hard eligibility gate,
   * not as a caption.
   */
  periodOpen: boolean
}

/** Whether the reporting period is closed, from the governed data-through date. */
export function reportingPeriodState(
  response: QuickBooksCfoPresentationResponse,
  // REQUIRED, not defaulted: this module is purity-guarded, so the clock read
  // belongs to the caller. A default would have put `new Date()` in here.
  today: { year: number; month: number },
): ReportingPeriodState {
  const dataThrough = response.source_state?.data_through || response.statements.profit_and_loss.periods.at(-1) || null
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(clean(dataThrough))
  if (!parts) return { dataThrough, periodOpen: false }
  const year = Number(parts[1])
  const month = Number(parts[2])
  const day = Number(parts[3])

  // A month is "still posting" only if it is the CURRENT calendar month. Any
  // earlier month is closed regardless of the day-of-month in the string.
  //
  // The previous rule was `day < lastDayOfMonth`, which read a period
  // IDENTIFIER as a partial through-date: the home brief passes `2026-06-01`
  // (June's id, a closed month), and day 1 of 30 looked like "one day in", so
  // every period-over-period card was rejected as open_period and the brief
  // rendered no highlights at all — while the board pack, which passes a real
  // through-date, behaved correctly. Same function, two callers, opposite
  // meanings for the same field.
  if (year !== today.year || month !== today.month) {
    return { dataThrough, periodOpen: false }
  }
  // Day 0 of the following month is the last day of this one.
  const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return { dataThrough, periodOpen: day < lastDayOfMonth }
}

export function buildHighlightEvidence(
  response: QuickBooksCfoPresentationResponse,
  insights: InsightCard[],
  // Same contract as `reportingPeriodState`: the clock belongs to the caller.
  // Defaults to the response's own period so existing callers keep working —
  // a period equal to its own month is closed, which is the safe reading.
  today?: { year: number; month: number },
): HighlightVisualEvidence[] {
  const output: HighlightVisualEvidence[] = []
  const arPeriods = (response.ar_analysis?.periods || []).map((period) => period.period)
  const resolvedToday = today ?? (() => {
    const parts = /^(\d{4})-(\d{2})-\d{2}$/.exec(clean(response.source_state?.data_through || ''))
    return parts ? { year: Number(parts[1]), month: Number(parts[2]) } : { year: 0, month: 0 }
  })()
  const { dataThrough, periodOpen } = reportingPeriodState(response, resolvedToday)
  const sourceEvidenceState = {
    freshness: (response.source_state?.new_data_available ? 'stale' : periodOpen ? 'provisional' : 'current') as HighlightVisualEvidence['freshness'],
    provenanceState: (['reconciled', 'passed'].includes(clean(response.source_state?.reconciliation_status)) ? 'governed' : 'unreconciled') as HighlightVisualEvidence['provenanceState'],
  }
  for (const card of insights) {
    const statement = statementFor(card, response)
    // While the month is still posting, measure against the last CLOSED period
    // rather than the partial one. A half-booked August against a whole July
    // reports a collapse that is an artifact of what has not posted yet — the
    // finding is withheld, but the right answer is to stand on ground that is
    // complete, not to show nothing.
    const closedIndex = periodOpen && statement && statement.periods.length >= 3
      ? statement.periods.length - 2
      : undefined
    const variance = statementVariancePoints(statement, closedIndex == null ? {} : { latestIndex: closedIndex })
    const trend = trendPoints(card)
    const concerns = arConcernPoints(response)
    const aging = agingPoints(response)
    const agingBridge = arAgingBridgePoints(response)
    const dualAxis = dualAxisPoints(card, response.metric_comparisons)
    const workingCapital = workingCapitalBridgePoints(response.statements?.cash_flow || null)
    const collision = cashCollisionPoints(response)
    const velocity = collectionsVelocityPoints(response)
    const mosaic = serviceLineMosaicPoints(response)
    const healthRange = healthRangePoints(card, response, dataThrough)
    const planRail = planDecisionPoints(response)
    const footballField = valuationFieldPoints(response)
    const revenueMovement = revenueMovementPoints(response)
    const classPressure = classPressurePoints(response)
    const forecastBand = forecastBandPoints(response)
    const scenarioLines = scenarioTrajectoryPoints(response)
    const pnlBridge = pnlBridgePoints(response.statements?.profit_and_loss || null)
    const trailingBenchmark = trailingBenchmarkPoints(statement)
    const category = norm(card.category || card.explain?.category)
    const detector = norm(card.detector)
    const subject = `${category} ${detector} ${norm(card.impact?.metric_id)}`
    const relevant = HIGHLIGHT_TEMPLATE_REGISTRY.filter((entry) =>
      entry.eligibleCategories.some((item) => category.includes(norm(item)))
      || entry.eligibleDetectors.some((item) => detector.includes(norm(item))),
    )
    for (const entry of relevant) {
      const provided = structuredEvidence(card, entry, dataThrough, sourceEvidenceState)
      if (provided) {
        output.push(provided)
        continue
      }
      if (entry.id === 'margin_leak_map' && variance.length >= entry.minimumPoints && /margin|profit/.test(subject)) {
        output.push(evidence(card, entry, card.headline, 'usd', statement?.periods || [], variance, ['quickbooks_cfo_statements_render'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'working_capital_current_map' && workingCapital.length >= entry.minimumPoints) {
        // Its own source now: net income → working-capital movements → operating
        // cash, which reconciles by construction. Previously this shared the
        // generic statement-variance array with four other templates.
        output.push(evidence(card, entry, 'Where profit became cash', 'usd', response.statements?.cash_flow?.periods || [], workingCapital, ['quickbooks_cfo_statements_render', 'cash_flow'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'working_capital_current_map' && variance.length >= entry.minimumPoints && /cash|liquidity|working_capital/.test(subject)) {
        // This compatibility path supports older/demo provider payloads that
        // publish current account positions but not the canonical cash-flow
        // bridge. Name the evidence for what it is; repeating a cash-trough
        // headline here made a balance view look like causal timing evidence.
        output.push(evidence(card, entry, 'Working-capital movements', 'usd', statement?.periods || [], variance, ['quickbooks_cfo_statements_render'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'variance_terrain' && variance.length >= entry.minimumPoints) {
        // topN is applied inside statementVariancePoints so the "All other"
        // remainder row survives; slicing here would drop it.
        output.push(evidence(card, entry, card.headline, 'usd', statement?.periods || [], variance, ['quickbooks_cfo_statements_render'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'expense_fingerprint' && trend.length >= entry.minimumPoints && /expense/.test(subject)) {
        output.push(evidence(card, entry, card.headline, card.impact.unit, trend.map((item) => clean(item.period)), trend, ['cfo_insights.trend'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'forecast_accuracy_fan' && trend.length >= entry.minimumPoints && /forecast_accuracy|forecast_confidence/.test(detector)) {
        output.push(evidence(card, entry, card.headline, card.impact.unit, trend.map((item) => clean(item.period)), trend, ['cfo_insights.trend', 'forecast_history'], dataThrough, sourceEvidenceState))
      } else if ((entry.id === 'debtor_constellation' || entry.id === 'concentration_skyline') && concerns.length >= entry.minimumPoints) {
        output.push(evidence(card, entry, card.headline, 'usd', arPeriods, concerns, ['quickbooks_cfo_ar_analysis.customer_concerns'], response.ar_analysis?.asOfDate || dataThrough, sourceEvidenceState))
      } else if (entry.id === 'aging_migration_ribbon' && aging.length >= entry.minimumPoints && arPeriods.length >= entry.minimumPeriods) {
        output.push(evidence(card, entry, card.headline, 'usd', arPeriods, aging, ['quickbooks_cfo_ar_analysis.periods'], response.ar_analysis?.asOfDate || dataThrough, sourceEvidenceState))
      } else if ((entry.id === 'legacy_kpi_sparkline' || entry.id === 'legacy_trend_line') && trend.length >= entry.minimumPoints) {
        output.push(evidence(card, entry, card.headline, card.impact.unit, trend.map((item) => clean(item.period)), trend, ['cfo_insights.trend', card.id], dataThrough, sourceEvidenceState))
      } else if ((entry.id === 'legacy_scenario_lines' || entry.id === 'runway_outcome_field') && scenarioLines.length >= entry.minimumPoints) {
        const periods = Array.from(new Set(scenarioLines.map((item) => item.period).filter((v): v is string => Boolean(v)))).sort()
        output.push(evidence(card, entry, 'Modelled scenarios', 'usd', periods, scenarioLines, ['quickbooks_cfo_forecast_lines', 'scenarios'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_forecast_band' && forecastBand.length >= entry.minimumPoints) {
        output.push(evidence(card, entry, 'Forecast range by period', 'usd', forecastBand.map((item) => item.period || '').filter(Boolean), forecastBand, ['quickbooks_cfo_forecast_lines'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'location_pressure_matrix' && classPressure.length >= entry.minimumPoints) {
        const periods = Array.from(new Set(classPressure.map((item) => item.period).filter((v): v is string => Boolean(v)))).sort()
        // Title names the basis: share of invoiced revenue, not operating cost.
        output.push(evidence(card, entry, 'Share of invoiced revenue by class', 'pct', periods, classPressure, ['quickbooks_cfo_ar_analysis', 'classRevenue'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'revenue_movement_staircase' && revenueMovement.length >= entry.minimumPoints) {
        // Title states the basis: this is invoiced revenue, not the P&L line.
        output.push(evidence(card, entry, 'Who moved revenue (invoiced)', 'usd', [((response.ar_analysis as any)?.revenueMovement?.current_period) || ''].filter(Boolean), revenueMovement, ['quickbooks_cfo_ar_analysis', 'revenueMovement'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_valuation_football_field' && footballField.length >= entry.minimumPoints) {
        output.push(evidence(card, entry, 'Equity value by method', 'usd', [], footballField, ['quickbooks_cfo_valuation'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'plan_decision_rail' && planRail.length >= entry.minimumPoints) {
        const closed = planRail[0]?.period || dataThrough
        output.push(evidence(card, entry, `Plan attainment through ${closed}`, 'usd', closed ? [closed] : [], planRail, ['quickbooks_cfo_plan_vs_actual'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_health_range_bar' && healthRange.length >= entry.minimumPoints) {
        output.push(evidence(card, entry, `${clean(card.headline)} against its healthy range`, clean(card.impact?.unit) || 'ratio', dataThrough ? [dataThrough] : ['current'], healthRange, ['cfo_ratio_cards'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'service_line_profit_mosaic' && mosaic.length >= entry.minimumPoints) {
        output.push(evidence(card, entry, 'Contribution by service line', 'usd', response.statements?.profit_and_loss?.periods?.slice(-1) || [], mosaic, ['quickbooks_cfo_service_lines'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'collections_velocity_curves' && velocity.length >= entry.minimumPoints) {
        const cohorts = Array.from(new Set(velocity.map((item) => item.period).filter((v): v is string => Boolean(v)))).sort()
        output.push(evidence(card, entry, 'How each month of billings drains', 'pct', cohorts, velocity, ['quickbooks_cfo_ar_analysis', 'cohorts'], response.ar_analysis?.asOfDate || dataThrough, sourceEvidenceState))
      } else if (entry.id === 'cash_collision_calendar' && collision.length >= entry.minimumPoints) {
        const trough = (response as any).cash_trough as CashTroughAttribution
        const weeks = Array.from(new Set(collision.map((item) => item.period).filter((value): value is string => Boolean(value)))).sort()
        output.push(evidence(card, entry, `Cash is tightest the week of ${trough.trough_week_start}`, 'usd', weeks, collision, ['quickbooks_cfo_cash_planning', 'cash_trough'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_waterfall_bridge' && pnlBridge.length >= entry.minimumPoints) {
        // The P&L's own calculation scheme, not a ranked account list.
        output.push(evidence(card, entry, 'How the result got from prior to current', 'usd', response.statements?.profit_and_loss?.periods || [], pnlBridge, ['quickbooks_cfo_statements_render', 'pnl_bridge'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_lollipop_benchmark' && trailingBenchmark.length >= entry.minimumPoints) {
        // Distance from each account's own trailing normal — a benchmark, which
        // is a different question from a period-over-period variance.
        output.push(evidence(card, entry, 'Accounts furthest from their own normal', 'usd', statement?.periods || [], trailingBenchmark, ['quickbooks_cfo_statements_render', 'trailing_benchmark'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_progress_ring' && ['pct', 'pct_pts', 'ratio', 'months'].includes(card.impact.unit) && progressReference(card, response) != null) {
        // A ring is progress toward a target, so it needs a real target. The
        // previous implementation invented one — `months / 18`, or dividing a
        // percentage by 100 — which drew a filled ring against a denominator
        // nobody chose and no source could defend. It now renders ONLY when a
        // governed benchmark exists, and otherwise the template is skipped and
        // the card falls through to one that can carry its evidence honestly.
        const reference = progressReference(card, response) as { target: number; label: string; lineage: string[] }
        const raw = Number(card.impact.value) || 0
        const progress = point({
          id: `${card.id}:progress`,
          label: card.headline,
          value: raw,
          unit: card.impact.unit,
          // The governed target the ring fills against, in the same unit.
          secondaryValue: reference.target,
          period: dataThrough,
          status: card.severity === 'bad' ? 'bad' : card.severity === 'watch' || card.severity === 'check' ? 'watch' : 'good',
          sourceLineage: reference.lineage,
        })
        output.push(evidence(card, entry, `${card.headline} vs ${reference.label}`, card.impact.unit, dataThrough ? [dataThrough] : ['current'], [progress], reference.lineage, dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_aging_bars' && aging.length >= entry.minimumPoints) {
        const latestPeriod = arPeriods.at(-1)
        const latestAging = latestPeriod ? aging.filter((item) => item.period === latestPeriod) : aging.slice(-3)
        if (latestAging.length >= entry.minimumPoints) output.push(evidence(card, entry, card.headline, 'usd', latestPeriod ? [latestPeriod] : arPeriods, latestAging, ['quickbooks_cfo_ar_analysis.periods'], response.ar_analysis?.asOfDate || dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_dual_axis_trend' && dualAxis.length >= entry.minimumPoints) {
        const pair = pairFor(clean(card.impact?.metric_id))
        output.push(evidence(card, entry, pair?.divergenceMeans || card.headline, pair?.primaryUnit || 'usd', [...new Set(dualAxis.map((item) => clean(item.period)))], dualAxis, ['quickbooks_cfo_metric_comparisons'], dataThrough, sourceEvidenceState))
      } else if (entry.id === 'legacy_ar_aging_bridge' && agingBridge.length >= entry.minimumPoints && arPeriods.length >= entry.minimumPeriods) {
        output.push(evidence(card, entry, card.headline, 'usd', arPeriods.slice(-2), agingBridge, ['quickbooks_cfo_ar_analysis.periods'], response.ar_analysis?.asOfDate || dataThrough, sourceEvidenceState))
      }
    }
  }
  return output.sort((a, b) => compareText(a.id, b.id))
}
